import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Label } from './Label';

interface ColorPickerProps {
  value?: string;
  onChange?: (value: string) => void;
  name?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  helperText?: string;
  presetColors?: string[];
  className?: string;
}

/**
 * ColorPicker - Color selection component with preset colors and native color picker
 * Supports preset color selection and custom color picking via native color input
 */
export const ColorPicker: React.FC<ColorPickerProps> = ({
  value = '#000000',
  onChange,
  name,
  label,
  required = false,
  disabled = false,
  readOnly = false,
  error,
  helperText,
  presetColors = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4'],
  className = '',
}) => {
  const { colors } = useTheme();
  const [selectedColor, setSelectedColor] = useState(value || '#000000');

  useEffect(() => {
    setSelectedColor(value || '#000000');
  }, [value]);

  const handlePresetClick = (preset: string) => {
    if (disabled || readOnly) return;
    setSelectedColor(preset);
    onChange?.(preset);
  };

  const handleColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || readOnly) return;
    const newColor = e.target.value;
    setSelectedColor(newColor);
    onChange?.(newColor);
  };

  const isPresetColor = presetColors
    .map((c) => c.toLowerCase())
    .includes(selectedColor.toLowerCase());

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
      )}
      <div
        className={cn(
          'relative border rounded-lg p-3 transition-colors',
          colors.borderLight,
          error ? 'border-red-500' : '',
          disabled || readOnly ? 'opacity-50 cursor-not-allowed' : colors.interactiveHover,
          className
        )}
      >
        {/* Color selection area */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Preset colors */}
          {presetColors.map((preset, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handlePresetClick(preset)}
              disabled={disabled || readOnly}
              className={cn(
                'w-8 h-8 rounded-lg border-2 transition-all cursor-pointer',
                selectedColor.toLowerCase() === preset.toLowerCase()
                  ? cn(colors.primaryBorderAll, 'ring-2 scale-110', colors.surfaceHover)
                  : colors.borderLight,
                disabled || readOnly ? 'cursor-not-allowed' : 'hover:scale-105',
                'focus:outline-none focus:ring-2 focus:ring-offset-1',
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : ''
              )}
              style={{ backgroundColor: preset }}
              title={preset}
            />
          ))}

          {/* Show selected color if not in presets */}
          {!isPresetColor && selectedColor && (
            <div
              className={cn(
                'w-8 h-8 rounded-lg border-2 ring-2',
            colors.primaryBorderAll,
            colors.surfaceHover,
                'flex items-center justify-center'
              )}
              style={{ backgroundColor: selectedColor }}
              title={selectedColor}
            />
          )}

          {/* Native color picker */}
          <div className="relative">
            <div
              className={cn(
                'w-8 h-8 rounded-lg border flex items-center justify-center',
                colors.borderLight,
                colors.surface,
                disabled || readOnly ? 'cursor-not-allowed' : cn('cursor-pointer', colors.interactiveHover)
              )}
            >
              <svg
                className={cn('w-4 h-4', colors.primaryFg)}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                />
              </svg>
            </div>
            <input
              type="color"
              name={name}
              value={selectedColor}
              onChange={handleColorInputChange}
              disabled={disabled || readOnly}
              className={cn(
                'absolute inset-0 w-full h-full opacity-0 cursor-pointer',
                disabled || readOnly ? 'cursor-not-allowed' : ''
              )}
              title="Pick custom color"
            />
          </div>
        </div>

        {/* Error or helper text */}
        {(error || helperText) && (
          <div className={cn('mt-2 text-xs', error ? colors.error : colors.textMuted)}>
            {error || helperText}
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorPicker;

